import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { ordersApi } from "@/features/production/services/ordersApi";
import { sowingInstructionsApi } from "@/features/production/services/sowingInstructionsApi";

export function ReportLookup({ kind, value, onChange, placeholder }: {
  kind: "order" | "instruction";
  value?: string;
  onChange: (id: string) => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [chosen, setChosen] = useState<{ id: string; label: string }>();
  const debouncedSearch = useDebounce(search.trim(), 300);
  const waiting = debouncedSearch !== search.trim();
  const query = useInfiniteQuery({
    queryKey: ["reports", "lookup", kind, debouncedSearch],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      if (kind === "order") {
        const result = await ordersApi.getPagedOrders({ search: debouncedSearch, pageNumber: pageParam, pageSize: 25 });
        return { items: result.items.map(x => ({ id: x.id, label: `${x.orderNumber} — ${x.customerName}` })), totalCount: result.totalCount };
      }
      // Status 6 is intentionally unused in SowingInstructionStatus; include every defined value.
      const result = await sowingInstructionsApi.getPagedSowingInstructionsByStatuses([0, 1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14], pageParam, 25, debouncedSearch);
      return { items: result.items.map(x => ({ id: x.id, label: x.instructionNumber || x.orderNumber || x.id })), totalCount: result.totalCount };
    },
    getNextPageParam: (last, pages) => pages.reduce((count, page) => count + page.items.length, 0) < last.totalCount ? pages.length + 1 : undefined,
    enabled: open,
    staleTime: 60_000,
  });
  return (
    <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setSearch(""); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} aria-label={kind === "order" ? "Order" : "Sowing instruction"} className="w-full justify-between font-normal">
          <span className="truncate">{value ? chosen?.id === value ? chosen.label : `Selected: ${value}` : placeholder}</span>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={kind === "order" ? "Search orders..." : "Search instruction number..."} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandGroup>
              <CommandItem value="__clear" onSelect={() => { onChange(""); setOpen(false); setSearch(""); }}>{placeholder}</CommandItem>
              {!waiting && query.data?.pages.flatMap(page => page.items).map(item => (
                <CommandItem key={item.id} value={item.id} onSelect={() => { setChosen(item); onChange(item.id); setOpen(false); setSearch(""); }}>{item.label}</CommandItem>
              ))}
            </CommandGroup>
            <CommandEmpty>No matches found.</CommandEmpty>
            {(waiting || query.isFetching) && <p role="status" className="p-3 text-sm text-muted-foreground">Loading...</p>}
            {query.isError && <Button variant="ghost" className="w-full" onClick={() => query.refetch()}>Could not load records. Retry</Button>}
            {!waiting && query.hasNextPage && <Button variant="ghost" className="w-full" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>Load more</Button>}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
